import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileText, Eye, Edit2, Trash2, Download,
  Clock, CheckCircle, XCircle, Truck, ArrowRight,
  Package, Send, IndianRupee, User, MoreVertical
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ListPageHeader from '../../components/shared/ListPageHeader';
import StaffFilterSelect from '../../components/shared/StaffFilterSelect';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { getApiErrorMessage } from '../../utils/apiErrors';

const CHALLAN_TYPE_LABELS = {
  SUPPLY_ON_APPROVAL: "Supply on Approval",
  JOB_WORK: "Job Work",
  CONSIGNMENT: "Consignment",
  EXHIBITION: "Exhibition / Demo",
  RETURNABLE: "Returnable",
  NON_RETURNABLE: "Non-Returnable",
  REPAIR: "Repair / Service",
  SAMPLE: "Sample",
  OTHER: "Other"
};

const DeliveryChallansPage = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const [challans, setChallans] = useState([]);
  const [stats, setStats] = useState({ total: 0, draft: 0, issued: 0, converted: 0, cancelled: 0, total_quantity: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    challan_type: 'all',
    search: '',
    from_date: '',
    to_date: ''
  });
  
  // Role-based visibility
  const [isOwner, setIsOwner] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    challan_number: '',
    customer_name: '',
    challan_date: { min: '', max: '' },
    challan_type: [],
    status: [],
    total_quantity: { min: '', max: '' },
    staff_member: ''
  });

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid) {
      fetchChallans();
    }
  }, [bid, filters.status, filters.challan_type, selectedStaffId]);

  const fetchChallans = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.challan_type !== 'all') params.append('challan_type', filters.challan_type);
      if (filters.search) params.append('search', filters.search);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      if (selectedStaffId) params.append('staff_id', selectedStaffId);

      const response = await api.get(`/delivery-challans?${params}`);
      setChallans(response.data.challans || []);
      if (response.data.stats) {
        setStats(response.data.stats);
      }
      setIsOwner(response.data.is_owner !== false);
      setStaffList(response.data.staff_list || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load delivery challans'));
    } finally {
      setLoading(false);
    }
  };

  // Apply all filters (search + column filters)
  const filteredChallans = challans.filter(challan => {
    // Global search
    const matchesSearch = !filters.search || 
      challan.challan_number?.toLowerCase().includes(filters.search.toLowerCase()) ||
      challan.customer_name?.toLowerCase().includes(filters.search.toLowerCase());
    
    // Column filters
    const matchesChallanNo = !columnFilters.challan_number || 
      challan.challan_number?.toLowerCase().includes(columnFilters.challan_number.toLowerCase());
    
    const matchesCustomer = !columnFilters.customer_name || 
      challan.customer_name?.toLowerCase().includes(columnFilters.customer_name.toLowerCase());
    
    const matchesChallanDate = (!columnFilters.challan_date?.min || new Date(challan.challan_date) >= new Date(columnFilters.challan_date.min)) &&
      (!columnFilters.challan_date?.max || new Date(challan.challan_date) <= new Date(columnFilters.challan_date.max));
    
    const matchesChallanType = columnFilters.challan_type.length === 0 || 
      columnFilters.challan_type.includes(challan.challan_type);
    
    // Staff member column filter (for owner view)
    const matchesStaffMember = !columnFilters.staff_member || 
      (challan.created_by_staff_name || '').toLowerCase().includes(columnFilters.staff_member.toLowerCase());
    
    const matchesStatus = columnFilters.status.length === 0 || 
      columnFilters.status.includes(challan.status);
    
    const qty = challan.totals?.total_quantity || 0;
    const matchesQuantity = (!columnFilters.total_quantity?.min || qty >= parseFloat(columnFilters.total_quantity.min)) &&
      (!columnFilters.total_quantity?.max || qty <= parseFloat(columnFilters.total_quantity.max));
    
    return matchesSearch && matchesChallanNo && matchesCustomer && matchesChallanDate && matchesChallanType && matchesStatus && matchesQuantity && matchesStaffMember;
  });

  const handleCreate = () => {
    navigate(`/business/${bid}/delivery-challans/new`);
  };

  const handleView = (challan) => {
    navigate(`/business/${bid}/delivery-challans/${challan.id}`);
  };

  const handleEdit = (challan, e) => {
    e.stopPropagation();
    if (challan.status !== 'DRAFT') {
      toast.error('Only DRAFT challans can be edited');
      return;
    }
    navigate(`/business/${bid}/delivery-challans/${challan.id}/edit`);
  };

  const handleIssue = (challanId, e) => {
    if (e) e.stopPropagation();
    setConfirmModal({
      title: 'Issue Delivery Challan',
      description: 'Items will be marked as dispatched and inventory will be reduced. This cannot be undone.',
      confirmLabel: 'Issue Challan',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      iconBgClass: 'bg-blue-100',
      iconClass: 'text-blue-600',
      icon: Send,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.patch(`/delivery-challans/${challanId}/issue`);
          toast.success('Delivery challan issued successfully');
          fetchChallans();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to issue challan'));
        }
      },
    });
  };

  const handleCancel = (challanId, e) => {
    if (e) e.stopPropagation();
    setConfirmModal({
      title: 'Cancel Delivery Challan',
      description: 'Issued challan will be voided and reserved inventory restored. This cannot be undone.',
      confirmLabel: 'Cancel Challan',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      icon: XCircle,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.patch(`/delivery-challans/${challanId}/cancel`);
          toast.success('Delivery challan cancelled');
          fetchChallans();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to cancel challan'));
        }
      },
    });
  };

  const handleConvertToInvoice = (challanId, e) => {
    if (e) e.stopPropagation();
    setConfirmModal({
      title: 'Convert to Sales Invoice',
      description: 'A new sales invoice will be created from this delivery challan.',
      confirmLabel: 'Convert',
      confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
      iconBgClass: 'bg-emerald-100',
      iconClass: 'text-emerald-600',
      icon: ArrowRight,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await api.post(`/delivery-challans/${challanId}/convert-to-invoice`);
          toast.success(`Converted to Invoice ${response.data.invoice?.invoice_number || ''}`);
          fetchChallans();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to convert to invoice'));
        }
      },
    });
  };

  const handleDownloadPdf = async (challanId, e) => {
    e.stopPropagation();
    try {
      const response = await api.get(`/delivery-challans/${challanId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DeliveryChallan_${challanId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error downloading PDF');
    }
  };

  const handleDelete = (challanId, e) => {
    if (e) e.stopPropagation();
    setConfirmModal({
      title: 'Delete Delivery Challan',
      description: 'This draft challan will be permanently deleted. This cannot be undone.',
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      icon: Trash2,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.delete(`/delivery-challans/${challanId}`);
          toast.success('Delivery challan deleted');
          fetchChallans();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete challan'));
        }
      },
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
      ISSUED: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Issued' },
      CONVERTED: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Converted' },
      CANCELLED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' }
    };
    const badge = badges[status] || badges.DRAFT;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCompactNumber = (num) => {
    if (num >= 10000000) return `${(num / 10000000).toFixed(1)}Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toFixed(0) || 0;
  };

  return (
    <div className="h-full flex flex-col" data-testid="delivery-challans-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Delivery Challans"
        stats={[
          { icon: FileText, label: 'Total', value: stats.total, color: 'slate' },
          { icon: Clock, label: 'Draft', value: stats.draft, color: 'amber' },
          { icon: Truck, label: 'Issued', value: stats.issued, color: 'blue' },
          { icon: CheckCircle, label: 'Converted', value: stats.converted, color: 'emerald' },
          { icon: Package, label: 'Total Qty', value: formatCompactNumber(stats.total_quantity), color: 'purple' },
        ]}
        ctaLabel="New Delivery Challan"
        onCtaClick={handleCreate}
        extraControls={isOwner && staffList.length > 0 ? (
          <StaffFilterSelect
            staffList={staffList}
            selectedStaffId={selectedStaffId}
            onChange={setSelectedStaffId}
          />
        ) : null}
      />

      {/* TABLE AREA */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading challans...</p>
                </div>
            </div>
          ) : filteredChallans.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Truck className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">
                {filters.search ? 'No challans found' : 'No delivery challans yet'}
              </p>
              <p className="text-[13px] text-slate-400 mt-1 mb-4">
                {filters.search ? 'Try adjusting your search' : 'Create your first delivery challan to track goods movement'}
              </p>
              {!filters.search && (
                <button
                  onClick={handleCreate}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-[13px] font-medium"
                >
                  Create Delivery Challan
                </button>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* SINGLE TABLE -- thead sticky to prevent column drift */}
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table">
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: '12%' }}>
                        <div className="flex items-center gap-1">
                          Challan #
                          <ColumnFilter
                            type="text"
                            value={columnFilters.challan_number}
                            onChange={(val) => setColumnFilters({...columnFilters, challan_number: val})}
                            placeholder="Filter challan..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '18%' }}>
                        <div className="flex items-center gap-1">
                          Customer
                          <ColumnFilter
                            type="text"
                            value={columnFilters.customer_name}
                            onChange={(val) => setColumnFilters({...columnFilters, customer_name: val})}
                            placeholder="Filter customer..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                        <div className="flex items-center gap-1">
                          Date
                          <ColumnFilter
                            type="date"
                            value={columnFilters.challan_date}
                            onChange={(val) => setColumnFilters({...columnFilters, challan_date: val})}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '14%' }}>
                        <div className="flex items-center gap-1">
                          Challan Type
                          <ColumnFilter
                            type="status"
                            value={columnFilters.challan_type}
                            onChange={(val) => setColumnFilters({...columnFilters, challan_type: val})}
                            options={[
                              { value: 'SUPPLY_ON_APPROVAL', label: 'Supply on Approval' },
                              { value: 'JOB_WORK', label: 'Job Work' },
                              { value: 'CONSIGNMENT', label: 'Consignment' },
                              { value: 'EXHIBITION', label: 'Exhibition' },
                              { value: 'RETURNABLE', label: 'Returnable' },
                              { value: 'NON_RETURNABLE', label: 'Non-Returnable' },
                              { value: 'REPAIR', label: 'Repair' },
                              { value: 'SAMPLE', label: 'Sample' }
                            ]}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '8%' }}>
                        <div className="flex items-center gap-1">
                          Status
                          <ColumnFilter
                            type="status"
                            value={columnFilters.status}
                            onChange={(val) => setColumnFilters({...columnFilters, status: val})}
                            options={[
                              { value: 'DRAFT', label: 'Draft' },
                              { value: 'ISSUED', label: 'Issued' },
                              { value: 'CONVERTED', label: 'Converted' },
                              { value: 'CANCELLED', label: 'Cancelled' }
                            ]}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal tl-th-right" style={{ width: '10%' }}>
                        <div className="flex items-center justify-end gap-1">
                          Total Qty
                          <ColumnFilter
                            type="number"
                            value={columnFilters.total_quantity}
                            onChange={(val) => setColumnFilters({...columnFilters, total_quantity: val})}
                          />
                        </div>
                      </th>
                      {/* Staff Member Column - Owner Only */}
                      {isOwner && (
                        <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                          <div className="flex items-center gap-1">
                            <User className="w-3.5 h-3.5" />
                            Staff
                            <ColumnFilter
                              type="text"
                              value={columnFilters.staff_member}
                              onChange={(val) => setColumnFilters({...columnFilters, staff_member: val})}
                              placeholder="Filter staff..."
                            />
                          </div>
                        </th>
                      )}
                      <th className="tl-th tl-th-teal" style={{ width: '60px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredChallans.map((challan, index) => (
                        <tr 
                          key={challan.id} 
                          className={`tl-tr tl-tr-clickable hover:bg-teal-50/40 ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                          onClick={() => handleView(challan)}
                        >
                          <td className="tl-td" style={{ width: '12%' }}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-[12px] font-medium text-teal-700">
                                  {challan.challan_number?.slice(-2) || 'DC'}
                                </span>
                              </div>
                              <span className="tl-cell-primary">{challan.challan_number}</span>
                            </div>
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '18%' }}>
                            {challan.customer_name || '—'}
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '10%' }}>
                            {formatDate(challan.challan_date)}
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '14%' }}>
                            <span className="text-[12px] text-slate-600">
                              {CHALLAN_TYPE_LABELS[challan.challan_type] || challan.challan_type || '—'}
                            </span>
                          </td>
                          <td className="tl-td" style={{ width: '8%' }}>
                            {getStatusBadge(challan.status)}
                          </td>
                          <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '10%' }}>
                            {challan.totals?.total_quantity || 0}
                          </td>
                          {/* Staff Member Column - Owner Only */}
                          {isOwner && (
                            <td className="tl-td" style={{ width: '10%' }}>
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                  <User className="w-3 h-3 text-slate-500" />
                                </div>
                                <span className="text-[12px] text-slate-600 truncate">
                                  {challan.created_by_staff_name || 'Owner'}
                                </span>
                              </div>
                            </td>
                          )}
                          <td className="tl-td" style={{ width: '60px' }} onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                                  title="Actions"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => handleView(challan)}>
                                  <Eye className="w-4 h-4 mr-2 text-slate-500" />
                                  View
                                </DropdownMenuItem>
                                {challan.status === 'DRAFT' && (
                                  <>
                                    <DropdownMenuItem onClick={(e) => handleEdit(challan, e)}>
                                      <Edit2 className="w-4 h-4 mr-2 text-slate-500" />
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => handleIssue(challan.id, e)}>
                                      <Send className="w-4 h-4 mr-2 text-emerald-600" />
                                      Issue Challan
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {challan.status === 'ISSUED' && (
                                  <DropdownMenuItem onClick={(e) => handleConvertToInvoice(challan.id, e)}>
                                    <ArrowRight className="w-4 h-4 mr-2 text-emerald-600" />
                                    Convert to Invoice
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={(e) => handleDownloadPdf(challan.id, e)}>
                                  <Download className="w-4 h-4 mr-2 text-slate-500" />
                                  Download PDF
                                </DropdownMenuItem>
                                {(challan.status === 'DRAFT' || challan.status === 'ISSUED') && (
                                  <>
                                    <DropdownMenuSeparator />
                                    {challan.status === 'DRAFT' ? (
                                      <DropdownMenuItem
                                        onClick={(e) => handleDelete(challan.id, e)}
                                        className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                      >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete
                                      </DropdownMenuItem>
                                    ) : (
                                      <DropdownMenuItem
                                        onClick={(e) => handleCancel(challan.id, e)}
                                        className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                      >
                                        <XCircle className="w-4 h-4 mr-2" />
                                        Cancel
                                      </DropdownMenuItem>
                                    )}
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* TABLE FOOTER */}
                <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                  <p>
                    Showing <span className="font-medium text-slate-700">{filteredChallans.length}</span> challans
                  </p>
                  <p>
                    Total Quantity: <span className="font-medium text-slate-700">{formatCompactNumber(stats.total_quantity)}</span>
                  </p>
                </div>
              </div>
            )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconClass={confirmModal?.iconClass}
        iconBgClass={confirmModal?.iconBgClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default DeliveryChallansPage;
